import React, { useMemo, useState } from "react";
import { bestIds } from "@/domain/projection/engine";
import { formatArs, formatNumber, formatPct, formatSignedArs } from "@/domain/projection/format";
import type { AlternativeComparisonRow, ProjectionAlternative, SimulationResult } from "@/domain/projection/types";
import { Button, ConfirmModal, Input, ModalShell } from "@/presentation/components/common";
import { ProjectionBarChart } from "./ProjectionBarChart";

type AlternativesPanelProps = {
  alternatives: ProjectionAlternative[];
  currentId: string | null;
  dirty: boolean;
  comparisonRows: AlternativeComparisonRow[];
  defaultName: string;
  onSaveCurrent: (name?: string, notes?: string) => void;
  onSaveAsNew: (name: string, notes: string) => void;
  onLoad: (id: string) => void;
  onDuplicate: (id: string) => void;
  onRename: (id: string, name: string, notes: string) => void;
  onDelete: (id: string) => void;
};

type Metric = {
  key: string;
  label: string;
  pick: (result: SimulationResult) => number;
  format: (value: number) => string;
  better: "max" | "min";
  emphasize?: boolean;
};

const METRICS: Metric[] = [
  { key: "resultado", label: "Resultado mensual", pick: (r) => r.resultado, format: formatSignedArs, better: "max", emphasize: true },
  { key: "mrr", label: "MRR total", pick: (r) => r.mrrTotal, format: formatArs, better: "max", emphasize: true },
  { key: "arpu", label: "ARPU", pick: (r) => r.arpu, format: formatArs, better: "max" },
  { key: "margin", label: "Margen", pick: (r) => r.marginPct, format: formatPct, better: "max" },
  { key: "clinics", label: "Clínicas", pick: (r) => r.clinics, format: (v) => formatNumber(v, 1), better: "max" },
  { key: "fixed", label: "Costos fijos", pick: (r) => r.fixedTotal, format: formatArs, better: "min" },
  { key: "variable", label: "Costos variables", pick: (r) => r.variableCost, format: formatArs, better: "min" },
  { key: "meta", label: "Costo Meta", pick: (r) => r.metaCost, format: formatArs, better: "min" },
  { key: "base", label: "Base + escala", pick: (r) => r.baseScaleMrr, format: formatArs, better: "max" },
  { key: "avanzado", label: "Avanzado incremental", pick: (r) => r.avanzadoMrr, format: formatArs, better: "max" },
  { key: "conexion", label: "MRR Conexión", pick: (r) => r.conexionMrr, format: formatArs, better: "max" },
  { key: "marketing", label: "MRR Marketing", pick: (r) => r.marketingMrr, format: formatArs, better: "max" },
  { key: "panel", label: "MRR Panel", pick: (r) => r.panelMrr, format: formatArs, better: "max" },
  { key: "perClinic", label: "Resultado / clínica", pick: (r) => r.resultadoPerClinic, format: formatSignedArs, better: "max" },
];

function formatSavedAt(iso: string | null): string {
  if (!iso) return "Sin guardar";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
}

export const AlternativesPanel: React.FC<AlternativesPanelProps> = ({
  alternatives,
  currentId,
  dirty,
  comparisonRows,
  defaultName,
  onSaveCurrent,
  onSaveAsNew,
  onLoad,
  onDuplicate,
  onRename,
  onDelete,
}) => {
  const [includeDraft, setIncludeDraft] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[] | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveMode, setSaveMode] = useState<"new" | "rename">("new");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState(defaultName);
  const [notes, setNotes] = useState("");
  const [pendingLoadId, setPendingLoadId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const visibleRows = useMemo(() => {
    const withoutDraft = includeDraft ? comparisonRows : comparisonRows.filter((row) => !row.isDraft);
    if (!selectedIds) return withoutDraft;
    return withoutDraft.filter((row) => selectedIds.includes(row.id));
  }, [comparisonRows, includeDraft, selectedIds]);

  const winners = useMemo(() => {
    return METRICS.reduce<Record<string, Set<string>>>((acc, metric) => {
      acc[metric.key] = bestIds(visibleRows, metric.pick, metric.better);
      return acc;
    }, {});
  }, [visibleRows]);

  const bestResultado = visibleRows.find((row) => winners.resultado?.has(row.id));
  const bestArpu = visibleRows.find((row) => winners.arpu?.has(row.id));
  const bestMargin = visibleRows.find((row) => winners.margin?.has(row.id));

  const openSaveAs = () => {
    setSaveMode("new");
    setEditingId(null);
    setName(defaultName);
    setNotes("");
    setSaveOpen(true);
  };

  const openRename = (alternative: ProjectionAlternative) => {
    setSaveMode("rename");
    setEditingId(alternative.id);
    setName(alternative.name);
    setNotes(alternative.notes);
    setSaveOpen(true);
  };

  const handleSaveModal = () => {
    if (saveMode === "rename" && editingId) {
      onRename(editingId, name, notes);
    } else {
      onSaveAsNew(name, notes);
    }
    setSaveOpen(false);
  };

  const requestLoad = (id: string) => {
    if (dirty && id !== currentId) {
      setPendingLoadId(id);
      return;
    }
    onLoad(id);
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((current) => {
      const base = current ?? comparisonRows.map((row) => row.id);
      const next = base.includes(id) ? base.filter((item) => item !== id) : [...base, id];
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="panel-card p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Alternativas guardadas</h3>
            <p className="mt-1 text-xs text-slate-500">
              Cada alternativa es un snapshot completo: costos, precios, cartera y mix. Cargar una no borra las otras.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => onSaveCurrent()}>
              {currentId ? "Actualizar actual" : "Guardar borrador"}
            </Button>
            <Button size="sm" onClick={openSaveAs}>
              Guardar como nueva
            </Button>
          </div>
        </div>
      </div>

      <div className="panel-table-wrap">
        <table className="min-w-full text-left text-sm">
          <thead className="panel-thead text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Comparar</th>
              <th className="px-3 py-2">Nombre</th>
              <th className="px-3 py-2">Guardada</th>
              <th className="px-3 py-2 text-right">Resultado</th>
              <th className="px-3 py-2 text-right">MRR</th>
              <th className="px-3 py-2 text-right">ARPU</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {alternatives.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-500">
                  Todavía no hay alternativas. Armá un modelo y guardalo como Alternativa 1, 2, 3…
                </td>
              </tr>
            ) : (
              alternatives.map((item) => {
                const row = comparisonRows.find((candidate) => candidate.id === item.id);
                const isCurrent = item.id === currentId;
                return (
                  <tr key={item.id} className="border-t border-slate-100 dark:border-gray-700">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={(selectedIds ?? comparisonRows.map((candidate) => candidate.id)).includes(item.id)}
                        onChange={() => toggleSelected(item.id)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800 dark:text-slate-100">
                        {item.name}
                        {isCurrent ? (
                          <span className="ml-2 rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-800 dark:bg-teal-900/50 dark:text-teal-200">
                            En edición{dirty ? " · cambios" : ""}
                          </span>
                        ) : null}
                      </div>
                      {item.notes ? <p className="mt-0.5 text-xs text-slate-500">{item.notes}</p> : null}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{formatSavedAt(item.savedAt)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${(row?.result.resultado ?? 0) >= 0 ? "text-teal-700 dark:text-teal-300" : "text-rose-600"}`}>
                      {row ? formatSignedArs(row.result.resultado) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{row ? formatArs(row.result.mrrTotal) : "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row ? formatArs(row.result.arpu) : "—"}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button type="button" className="text-xs font-medium text-teal-700 dark:text-teal-300" onClick={() => requestLoad(item.id)}>
                          Cargar
                        </button>
                        <button type="button" className="text-xs font-medium text-slate-600 dark:text-slate-300" onClick={() => onDuplicate(item.id)}>
                          Duplicar
                        </button>
                        <button type="button" className="text-xs font-medium text-slate-600 dark:text-slate-300" onClick={() => openRename(item)}>
                          Renombrar
                        </button>
                        <button type="button" className="text-xs font-medium text-rose-600" onClick={() => setPendingDeleteId(item.id)}>
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {visibleRows.length >= 2 ? (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="panel-stat">
              <p className="panel-stat-label">Mejor resultado</p>
              <p className="panel-stat-value text-teal-700 dark:text-teal-300">{bestResultado?.name ?? "—"}</p>
              <p className="mt-1 text-[11px] text-slate-400">{bestResultado ? formatSignedArs(bestResultado.result.resultado) : ""}</p>
            </div>
            <div className="panel-stat">
              <p className="panel-stat-label">Mejor ARPU</p>
              <p className="panel-stat-value">{bestArpu?.name ?? "—"}</p>
              <p className="mt-1 text-[11px] text-slate-400">{bestArpu ? formatArs(bestArpu.result.arpu) : ""}</p>
            </div>
            <div className="panel-stat">
              <p className="panel-stat-label">Mejor margen</p>
              <p className="panel-stat-value">{bestMargin?.name ?? "—"}</p>
              <p className="mt-1 text-[11px] text-slate-400">{bestMargin ? formatPct(bestMargin.result.marginPct) : ""}</p>
            </div>
          </div>

          <ProjectionBarChart
            title="Resultado mensual por alternativa"
            caption="Cada barra usa el snapshot guardado. El borrador actual se incluye si está tildado."
            items={visibleRows.map((row) => ({
              label: row.name,
              value: row.result.resultado,
            }))}
          />

          <div className="panel-table-wrap">
            <div className="border-b border-slate-100 px-4 py-3 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Comparación</h3>
              <p className="text-xs text-slate-500">El valor más conveniente de cada fila queda marcado.</p>
              <label className="mt-2 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <input
                  type="checkbox"
                  checked={includeDraft}
                  onChange={(event) => setIncludeDraft(event.target.checked)}
                />
                Incluir el borrador en edición
              </label>
            </div>
            <div className="panel-table-scroll-hint">Deslizá horizontalmente para ver todas las alternativas.</div>
            <table className="min-w-full text-left text-sm">
              <thead className="panel-thead text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="sticky left-0 z-10 bg-slate-50 px-3 py-2 dark:bg-gray-900">Métrica</th>
                  {visibleRows.map((row) => (
                    <th key={row.id} className="px-3 py-2 text-right">
                      {row.name}
                      {row.isDraft ? <span className="block text-[10px] font-medium normal-case tracking-normal text-amber-600">Borrador</span> : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {METRICS.map((metric) => (
                  <tr key={metric.key} className="border-t border-slate-100 dark:border-gray-700">
                    <th className={`sticky left-0 z-10 bg-white px-3 py-2 text-left font-medium dark:bg-gray-800 ${metric.emphasize ? "text-slate-900 dark:text-white" : "text-slate-600 dark:text-slate-300"}`}>
                      {metric.label}
                    </th>
                    {visibleRows.map((row) => {
                      const value = metric.pick(row.result);
                      const isBest = visibleRows.length > 1 && winners[metric.key]?.has(row.id);
                      return (
                        <td
                          key={row.id}
                          className={`px-3 py-2 text-right tabular-nums ${
                            isBest
                              ? "bg-teal-50 font-semibold text-teal-800 dark:bg-teal-900/30 dark:text-teal-200"
                              : "text-slate-800 dark:text-slate-100"
                          }`}
                        >
                          {metric.format(value)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="panel-card p-4 text-sm text-slate-500">
          Guardá al menos dos alternativas para comparar cuál rinde más.
        </div>
      )}

      <ModalShell
        isOpen={saveOpen}
        onClose={() => setSaveOpen(false)}
        title={saveMode === "rename" ? "Renombrar alternativa" : "Guardar como nueva alternativa"}
        subtitle="El snapshot conserva costos, precios, cartera y supuestos de adopción."
        footer={
          <>
            <Button onClick={handleSaveModal}>{saveMode === "rename" ? "Guardar nombre" : "Guardar alternativa"}</Button>
            <Button variant="secondary" onClick={() => setSaveOpen(false)}>Cancelar</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Nombre" value={name} onChange={(event) => setName(event.target.value)} placeholder="Alternativa 1" />
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Notas</span>
            <textarea
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Ej. dólar 1800, 50% conexión, sin deuda"
            />
          </label>
        </div>
      </ModalShell>

      <ConfirmModal
        isOpen={Boolean(pendingLoadId)}
        onClose={() => setPendingLoadId(null)}
        onConfirm={() => {
          if (pendingLoadId) onLoad(pendingLoadId);
          setPendingLoadId(null);
        }}
        title="Cargar alternativa"
        message="Hay cambios sin guardar en el modelo actual. Si cargás otra alternativa, esos cambios del borrador se pierden (las alternativas ya guardadas no se tocan)."
        confirmText="Cargar igual"
        variant="danger"
      />

      <ConfirmModal
        isOpen={Boolean(pendingDeleteId)}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={() => {
          if (pendingDeleteId) onDelete(pendingDeleteId);
          setPendingDeleteId(null);
        }}
        title="Eliminar alternativa"
        message="Se borra este snapshot. El resto de las alternativas se mantiene."
        confirmText="Eliminar"
        variant="danger"
      />
    </div>
  );
};
